import { EmptyState } from '@chopchop-se/ui';
import type { PermissionKey } from '@warehouse/shared';
import { ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useLocation } from 'react-router';

import { FullPageSpinner } from '../components/FullPageSpinner';
import { useAuthStore, userHasPermission } from './auth-store';

/**
 * Route guards. `RequireAuth` wraps every non-public route; `RequirePermission`
 * wraps the subtrees whose whole area is permission-gated.
 *
 * These hide chrome. They are NOT security — the server enforces the same keys
 * on every endpoint. A guard that lets someone through only means they will see
 * an empty page full of 403s.
 */

export function RequireAuth({ children }: { children?: ReactNode }): ReactNode {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    // `from` survives the EMS round trip in router state, so a deep link
    // (a QR code pointing at /portal/receive/<id>) still lands where it meant to.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children ?? <Outlet />;
}

export function RequirePermission({
  permissions,
  children,
}: {
  /** OR semantics, exactly like `@Permissions(a, b)` on the server. */
  permissions: readonly PermissionKey[];
  children?: ReactNode;
}): ReactNode {
  const user = useAuthStore((s) => s.user);
  const granted = useAuthStore((s) => s.permissions);
  const status = useAuthStore((s) => s.status);

  if (status === 'loading') return <FullPageSpinner />;
  if (!userHasPermission(user, granted, permissions)) return <NoAccess />;
  return children ?? <Outlet />;
}

export function NoAccess(): ReactNode {
  const { t } = useTranslation('shell');
  return (
    <div className="mx-auto max-w-xl py-16">
      <EmptyState
        icon={<ShieldOff />}
        title={t('auth.noAccessTitle')}
        description={t('auth.noAccessDescription')}
        action={
          <Link
            to="/"
            className="rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            {t('auth.backToStart')}
          </Link>
        }
      />
    </div>
  );
}
